import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Save, FileEdit, Microscope, Bug } from 'lucide-react';


const SaveTemplateDialog = ({
  open,
  onClose,
  onSave,
  isUpdating = false,
  currentTemplate = null
}) => {
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templateHasAlgae, setTemplateHasAlgae] = useState(true);
  const [templateHasZoo, setTemplateHasZoo] = useState(true);
  
  useEffect(() => {
    if (open && isUpdating && currentTemplate) {
      if (currentTemplate.id === 'default-template') {
        setTemplateName(`Min Tilpassede Template ${new Date().toLocaleDateString('no-NO')}`);
        setTemplateDescription('Basert på standard-template med mine endringer');
      } else {
        setTemplateName(currentTemplate.name || '');
        setTemplateDescription(currentTemplate.description || '');
      }
      setTemplateHasAlgae(currentTemplate.config?.has_algae !== false);
      setTemplateHasZoo(currentTemplate.config?.has_zooplankton !== false);
    } else if (open && !isUpdating) {
      setTemplateName('');
      setTemplateDescription('');
      setTemplateHasAlgae(true);
      setTemplateHasZoo(true);
    }
  }, [open, isUpdating, currentTemplate]);
  
  const neitherSelected = !templateHasAlgae && !templateHasZoo;
  
  const handleSave = () => {
    if (!templateName.trim() || neitherSelected) return;
    
    onSave({
      name: templateName.trim(),
      description: templateDescription.trim(),
      has_algae: templateHasAlgae,
      has_zooplankton: templateHasZoo
    });
    
    setTemplateName('');
    setTemplateDescription('');
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            {isUpdating ? (
              <>
                <FileEdit className="w-5 h-5 text-blue-600" />
                <span>Oppdater Template</span>
              </>
            ) : (
              <>
                <Save className="w-5 h-5 text-teal-600" />
                <span>Lagre som ny Template</span>
              </>
            )}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <Label htmlFor="template-name" className="text-sm font-semibold">
              Namn på template*
            </Label>
            <Input
              id="template-name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="F.eks. P02 Sommerprøver"
              className="mt-1"
            />
          </div>
          
          <div>
            <Label htmlFor="template-description" className="text-sm font-semibold">
              Beskrivelse (valfritt)
            </Label>
            <Textarea
              id="template-description"
              value={templateDescription}
              onChange={(e) => setTemplateDescription(e.target.value)}
              placeholder="Beskriv kva som gjer denne templaten unik..."
              className="mt-1 min-h-[80px]"
            />
          </div>
          
          {/* Section checkboxes */}
          <div>
            <Label className="text-sm font-semibold mb-2 block">Seksjonar i skjema*</Label>
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={templateHasAlgae}
                  onChange={(e) => setTemplateHasAlgae(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  data-testid="template-has-algae"
                />
                <Microscope className="w-4 h-4 text-teal-600" />
                <div>
                  <span className="text-sm font-medium text-slate-800">Algeprøver</span>
                  <p className="text-xs text-slate-500">Kiselalger, svepeflagellater, osv.</p>
                </div>
              </label>
              
              <label className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={templateHasZoo}
                  onChange={(e) => setTemplateHasZoo(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  data-testid="template-has-zoo"
                />
                <Bug className="w-4 h-4 text-blue-600" />
                <div>
                  <span className="text-sm font-medium text-slate-800">Zooplankton</span>
                  <p className="text-xs text-slate-500">Håvtrekk, visuelt, osv.</p>
                </div>
              </label>
            </div>
            
            {neitherSelected && (
              <p className="text-xs text-red-500 mt-1.5">Minst ein seksjon må vere valt</p>
            )}
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-800">
              {isUpdating ? (
                <>
                  <strong>Oppdatering:</strong> Dette vil oppdatere den eksisterande templaten med noverande organismar og lokalitetar.
                </>
              ) : (
                <>
                  <strong>Tips:</strong> Templaten vil lagre noverande valde organismar og lokalitetar, slik at du raskt kan laste dei inn neste gong.
                </>
              )}
            </p>
          </div>
        </div>
        
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
          >
            Avbryt
          </Button>
          <Button
            onClick={handleSave}
            disabled={!templateName.trim() || neitherSelected}
            className="bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700"
          >
            {isUpdating ? (
              <>
                <FileEdit className="w-4 h-4 mr-2" />
                Oppdater
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Lagre
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SaveTemplateDialog;
